import React, { useState, useEffect } from 'react'
import { Box, Container, Typography, Paper, Avatar, Stack, Rating } from '@mui/material'
import { useLanguage } from '../context/LanguageContext'

interface Review {
  id: string
  customerName: string
  location: string
  rating: number
  reviewText: string
  planType?: string
  isFeatured: boolean
}

const Testimonials: React.FC = () => {
  const { t } = useLanguage()
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch('/api/reviews')
        const data = await response.json()

        if (data.success && data.reviews) {
          // Only show up to 6 reviews for the testimonials section
          setReviews(data.reviews.slice(0, 6))
        }
      } catch (error) {
        console.error('Error fetching reviews:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchReviews()
  }, [])

  // Helper function to get initials from customer name
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0]
    }
    return parts[0][0] || 'U'
  }

  // Show loading state or fallback to empty if no reviews
  if (loading || reviews.length === 0) {
    return null
  }

  return (
    <Box component="section" sx={{ 
      bgcolor: '#1E2227', 
      backgroundImage: 'radial-gradient(ellipse at top, rgba(25, 118, 210, 0.05) 0%, transparent 50%)',
      py: { xs: 8, md: 8 },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
      }
    }}>
      <Container maxWidth="lg">
        {/* Section header */}
        <Typography
          sx={{
            color: '#8A8F98',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            fontWeight: 900,
            fontSize: 18,
            mb: 1.5,
          }}
        >
          {t('testimonials.title')}
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: 'white',
            fontWeight: 800,
            fontSize: { xs: 32, md: 42 },
            mb: 2,
            lineHeight: 1.2
          }}
        >
          {t('testimonials.heading')}
        </Typography>
        <Typography
          sx={{
            color: '#A0AEC0',
            fontSize: { xs: 17, md: 19 },
            mb: 6,
            lineHeight: 1.6,
            maxWidth: 600
          }}
        >
          {t('testimonials.subtitle')}
        </Typography>

        {/* Testimonials grid with staggered layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Top row - 2 cards */}
          <Box sx={{
            display: 'flex',
            gap: 3,
            justifyContent: 'center',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {reviews.slice(0, 2).map((review) => (
              <Box key={review.id} sx={{
                flex: { xs: '1 1 100%', md: '0 0 calc(50% - 12px)' },
                maxWidth: { xs: '100%', md: 'calc(50% - 12px)' }
              }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.2), 0 6px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                      opacity: 0,
                      transition: 'opacity 200ms ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.2)',
                      '&::before': {
                        opacity: 1,
                      }
                    }
                  }}
                >
                  {/* Customer avatar, name, and location */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: '#F3F4F6',
                        color: '#6B7280',
                        width: 40,
                        height: 40,
                        fontSize: '0.875rem',
                        fontWeight: 700
                      }}
                    >
                      {getInitials(review.customerName)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#1F2937',
                          fontSize: '1rem',
                          lineHeight: 1.2
                        }}
                      >
                        {review.customerName}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#9CA3AF',
                          fontSize: '0.8rem',
                          lineHeight: 1
                        }}
                      >
                        {review.location}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Star rating */}
                  <Box sx={{ mb: 1.5 }}>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>

                  {/* Review text */}
                  <Typography
                    sx={{
                      color: '#4B5563',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                      flex: 1
                    }}
                  >
                    "{review.reviewText}"
                  </Typography>

                  {/* Plan type badge */}
                  {review.planType && (
                    <Typography
                      sx={{
                        color: '#1976d2',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        mt: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      {review.planType}
                    </Typography>
                  )}
                </Paper>
              </Box>
            ))}
          </Box>

          {/* Bottom row - remaining cards (up to 4) */}
          {reviews.length > 2 && (
            <Box sx={{
              display: 'flex',
              gap: 3,
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { sm: 'wrap', md: 'nowrap' }
            }}>
              {reviews.slice(2, 6).map((review) => (
                <Box key={review.id} sx={{
                  flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(25% - 18px)' },
                  maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' }
                }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'white',
                      borderRadius: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.2), 0 6px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                        opacity: 0,
                        transition: 'opacity 200ms ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.2)',
                        '&::before': {
                          opacity: 1,
                        }
                      }
                    }}
                  >
                    {/* Customer avatar, name, and location */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar
                        sx={{
                          bgcolor: '#F3F4F6',
                          color: '#6B7280',
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem',
                          fontWeight: 700
                        }}
                      >
                        {getInitials(review.customerName)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#1F2937',
                            fontSize: '1rem',
                            lineHeight: 1.2
                          }}
                        >
                          {review.customerName}
                        </Typography>
                        <Typography
                          sx={{
                            color: '#9CA3AF',
                            fontSize: '0.8rem',
                            lineHeight: 1
                          }}
                        >
                          {review.location}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Star rating */}
                    <Box sx={{ mb: 1.5 }}>
                      <Rating value={review.rating} readOnly size="small" />
                    </Box>

                    {/* Review text */}
                    <Typography
                      sx={{
                        color: '#4B5563',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                        flex: 1
                      }}
                    >
                      "{review.reviewText}"
                    </Typography>

                    {/* Plan type badge */}
                    {review.planType && (
                      <Typography
                        sx={{
                          color: '#1976d2',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          mt: 1.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        {review.planType}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default Testimonials
